import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Database, Shield, Globe, CheckCircle, Server } from "lucide-react";

// --- Helper Components ---

// Fixed: Added type for 'rating'
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <CheckCircle className="w-3 h-3 mr-1 text-blue-800" />
    Verified
  </span>
);

const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'PostgreSQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg' },
    { name: 'pgAdmin', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/29/PgAdmin_logo.png' },
    { name: 'SQL Shell', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/bash/bash-original.svg' },
    { name: 'DBeaver', logo: 'https://upload.wikimedia.org/wikipedia/commons/b/b5/DBeaver_logo.svg' },
    { name: 'Python', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg' },
    { name: 'Docker', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg' },
    { name: 'PostGIS', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/87/PostGIS_logo.png/1200px-PostGIS_logo.png' },
    { name: 'AWS RDS', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg' },
  ];

  return (
    <section className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-black font-aligran">
          Tools You Will <span className="text-blue-800">Master</span>
        </h2>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-8 mb-8">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <img
                src={tool.logo}
                alt={`${tool.name} Logo`}
                className="w-16 h-16 mb-2 object-contain"
              />
              <p className="text-sm font-semibold text-black mt-2">{tool.name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PostgreSQL = () => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  // Fixed: Added type 'React.FormEvent'
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Inquiry Sent!");
  };

  // Fixed: Added type 'React.ChangeEvent<HTMLInputElement>'
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % 5);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    { id: 1, name: "Arun Bose", review: "PostgreSQL's JSON support is amazing. This course helped me switch from MongoDB for a hybrid project.", rating: 5, profileImg: "https://randomuser.me/api/portraits/men/44.jpg", source: "Google Reviews" },
    { id: 2, name: "Sakshi Tanwar", review: "Advanced SQL features like Window Functions and CTEs were explained perfectly.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/48.jpg", source: "LinkedIn" },
    { id: 3, name: "Vikram Das", review: "The module on Performance Tuning and Indexing was eye-opening for my backend career.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/51.jpg", source: "Website Feedback" },
    { id: 4, name: "Nisha Patel", review: "Best course for advanced database concepts. The PostGIS geographic data part was unique.", rating: 5, profileImg: "https://randomuser.me/api/portraits/women/55.jpg", source: "LinkedIn" },
    { id: 5, name: "Rajat Verma", review: "Learned how to deploy Postgres on AWS RDS. Very practical training.", rating: 4, profileImg: "https://randomuser.me/api/portraits/men/60.jpg", source: "Google Reviews" },
  ];

  const currentVisibleTestimonials = [
    testimonials[currentTestimonialIndex],
    testimonials[(currentTestimonialIndex + 1) % 5],
    testimonials[(currentTestimonialIndex + 2) % 5],
  ];

  const curriculum = [
    { title: "Module 1: Introduction to PostgreSQL", description: "Architecture, MVCC (Concurrency), Installation, and connecting via pgAdmin & PSQL." },
    { title: "Module 2: Advanced SQL Queries", description: "CTEs (Common Table Expressions), Recursive Queries, and Window Functions." },
    { title: "Module 3: JSON & NoSQL Features", description: "Storing JSONB data, querying JSON, and Indexing JSON documents within SQL." },
    { title: "Module 4: Performance Tuning", description: "Explain Analyze, Index types (B-Tree, Hash, GIN, GiST), and query optimization." },
    { title: "Module 5: Stored Procedures & PL/pgSQL", description: "Writing complex logic inside DB using PL/pgSQL, Triggers, and User-Defined Functions." },
    { title: "Module 6: Transactions & Concurrency", description: "ACID properties, Isolation Levels, Locking mechanisms, and Deadlock handling." },
    { title: "Module 7: Security & Administration", description: "Roles, Privileges, Backup (pg_dump), Restore, and Point-in-Time Recovery." },
    { title: "Module 8: PostGIS (Geospatial Data)", description: "Introduction to storing and querying location/map data (GIS)." },
    { title: "Module 9: High Availability", description: "Replication, Load Balancing concepts, and deploying on Cloud (AWS RDS)." },
  ];

  const highlights = [
    { title: 'Advanced SQL', desc: 'Master CTEs & Window Functions.', img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070' },
    { title: 'JSON Support', desc: 'NoSQL power in SQL DB.', img: 'https://images.unsplash.com/photo-1558494949-ef526b0042a0?q=80&w=2070' },
    { title: 'Performance', desc: 'Indexing & Query Tuning.', img: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070' },
    { title: 'Geospatial', desc: 'PostGIS for Map Data.', img: 'https://images.unsplash.com/photo-1569336415962-a4bd9f69cd83?q=80&w=2069' },
    { title: 'Reliability', desc: 'ACID & Transaction safety.', img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?q=80&w=2070' },
    { title: 'Administration', desc: 'Backup & Recovery Mastery.', img: 'https://images.unsplash.com/photo-1558494949-ef526b0042a0?q=80&w=2070' },
    { title: 'Expert Faculty', desc: 'Learn from DB Architects.', img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070' },
    { title: 'Certification', desc: 'PostgreSQL Specialist.', img: 'https://images.unsplash.com/photo-1589330273594-fade1ee91647?q=80&w=2070' },
    { title: 'Placement', desc: 'Top tier Backend Jobs.', img: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=2069' },
  ];

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>PostgreSQL Course | Advanced Relational Database</title>
        <meta name="description" content="Master PostgreSQL. Learn Advanced SQL, JSON, and Performance Tuning." />
      </Helmet>

      {/* Hero */}
      <section className="relative h-[550px] flex items-center bg-black">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1544383835-bda2bc66a55d?q=80&w=2121"
            alt="PostgreSQL"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-blue-900/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">
          <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }}>
            <span className="inline-block px-3 py-1 mb-4 border border-blue-500 rounded-full bg-blue-900/30 text-blue-400 text-sm font-semibold">
              WORLD'S MOST ADVANCED DB
            </span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white font-aligran">
              Master <span className="text-blue-500 block">PostgreSQL</span>
            </h1>
            <p className="text-xl text-gray-300 mt-6 mb-8">
              Build reliable, robust, and high-performance applications with the world's most advanced open-source database.
            </p>
            <button
              onClick={() =>
                document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-blue-800 hover:bg-blue-900 text-white font-bold py-4 px-10 rounded-lg shadow-lg hover:scale-105 transition"
            >
              Start Learning
            </button>
          </motion.div>
          <motion.div className="flex justify-end">
            <Database size={220} className="text-blue-500 opacity-80 drop-shadow-lg" />
          </motion.div>
        </div>
      </section>

      {/* Why Learn */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50 text-center">
        <h2 className="text-4xl font-extrabold mb-14 font-aligran">
          Why Learn <span className="text-blue-800">PostgreSQL?</span>
        </h2>
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Globe className="mx-auto mb-4 text-blue-800" size={40} />
            <h3 className="font-bold text-xl mb-3">Enterprise Standard</h3>
            <p className="text-gray-600">Preferred by Apple, Instagram, and Spotify for critical data.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Shield className="mx-auto mb-4 text-green-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Reliability</h3>
            <p className="text-gray-600">Known for data integrity, ACID compliance, and fault tolerance.</p>
          </div>
          <div className="p-8 border border-gray-100 rounded-2xl bg-white shadow-md hover:-translate-y-2 transition duration-300">
            <Server className="mx-auto mb-4 text-purple-600" size={40} />
            <h3 className="font-bold text-xl mb-3">Hybrid Power</h3>
            <p className="text-gray-600">Combines SQL relational power with NoSQL JSON flexibility.</p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-blue-800">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {highlights.map((h, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md overflow-hidden flex h-40 hover:shadow-xl transition hover:-translate-y-1"
              >
                <div className="w-1/2 p-4 flex flex-col justify-center">
                  <h3 className="font-bold text-lg">{h.title}</h3>
                  <p className="text-sm text-gray-600">{h.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img
                    src={h.img}
                    alt={h.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">
            Detailed <span className="text-blue-800">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculum.map((m, i) => (
              <details key={i} className="group border rounded-lg bg-gray-50">
                <summary className="cursor-pointer px-6 py-4 font-semibold flex justify-between">
                  {m.title}
                  <span className="group-open:rotate-180 transition">▼</span>
                </summary>
                <div className="px-6 py-4 border-t text-gray-700">{m.description}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <ToolsYouWillMaster />

      {/* FAQ & Form */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 font-aligran">FAQ</h2>
          <div className="space-y-4">
            <details className="bg-gray-50 border p-4 rounded-lg">
              <summary className="font-bold">Is it harder than MySQL?</summary>
              <p className="mt-2 text-sm">
                Slightly more advanced, but we start from basics. It offers more features than MySQL.
              </p>
            </details>
            <details className="bg-gray-50 border p-4 rounded-lg">
              <summary className="font-bold">JSON in SQL?</summary>
              <p className="mt-2 text-sm">
                Yes! Postgres handles JSON natively, making it a great alternative to MongoDB in some cases.
              </p>
            </details>
          </div>
        </div>
        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-blue-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-blue-800">Book Seat</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                className="w-full border p-3 rounded"
                placeholder="Name"
                name="firstName" // Fixed name
                onChange={handleInputChange} // Fixed handler
              />
              <input 
                className="w-full border p-3 rounded" 
                placeholder="Email" 
                name="businessEmail" 
                onChange={handleInputChange}
              />
              <input 
                className="w-full border p-3 rounded" 
                placeholder="Phone" 
                name="phone"
                onChange={handleInputChange}
              />
              <button className="w-full bg-blue-800 text-white font-bold py-3 rounded hover:bg-blue-900 transition">
                ENQUIRE
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-gray-50 py-20 border-t">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-12 font-aligran">
            Success <span className="text-blue-800">Stories</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {currentVisibleTestimonials.map((t) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white p-6 rounded-xl border shadow-sm"
                >
                  <div className="flex items-center mb-4">
                    <img
                      src={t.profileImg}
                      className="w-12 h-12 rounded-full border-2 border-blue-800 mr-3"
                      alt={t.name}
                    />
                    <div>
                      <h4 className="font-bold">{t.name}</h4>
                      <span className="text-xs text-gray-500">{t.source}</span>
                    </div>
                  </div>
                  <StarRating rating={t.rating} />
                  <p className="text-gray-700 text-sm mt-2">"{t.review}"</p>
                  <VerifiedBadge />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostgreSQL;